import { useQuery } from "@tanstack/react-query";
import { addDays, formatISO, startOfDay } from "date-fns";
import {
  ArrowRight,
  Boxes,
  CircleDollarSign,
  Handshake,
  ListChecks,
  MapPin,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api, queryString } from "../../api/client";
import type { BookingTask, EventRecord, Invoice, ListResponse, Sponsorship } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows, membershipAllowsIn } from "../../auth/permissions";
import { formatMoney } from "../../lib/format";
import { ErrorPanel, PageHeader } from "../../ui/components";
import { DashboardSkeleton } from "../../ui/skeletons";
import type { AssetReservation, InventoryReservation } from "../inventory/inventoryTypes";
import {
  type HomeRecord,
  NextUpSection,
  queryCount,
  ReadinessSection,
  type ReadinessTile,
  timeGreeting,
  UpcomingSection,
} from "./homeSections";

const terminalStatuses = new Set(["completed", "cancelled", "rejected", "declined", "expired"]);

function toRecord(event: EventRecord): HomeRecord {
  // An event holds its rooms directly, or takes them from the hire it is delivered under, and the
  // server has already resolved which of those applies by the time the allocations arrive here.
  return {
    id: event.id,
    reference: event.reference,
    title: event.title,
    status: event.status,
    start: event.start,
    timezone: event.timezone,
    summary: event.summary,
    customerName: event.customerName,
    attendeeCount: event.attendeeCount,
    venueNames: [...new Set(event.allocations.map((allocation) => allocation.venueName))],
    locationNames: [...new Set(event.allocations.map((allocation) => allocation.locationName))],
    path: `/events/${event.id}`,
  };
}

// Home for the client's own programme. This is the half the product leads with: the organisation
// running its own events, with the lettings that pay for the building kept on their own home so
// neither is read as the other.
export function EventsHomePage() {
  const auth = useAuth();
  const membership = auth.session?.selectedMembership;
  // Every question on this page is about the client's own programme, so each is asked of the role
  // held on that side. A finance clerk who keeps the lettings books alone is not shown what the
  // client owes on their own season, because that is not their side of the building.
  const canViewFinance = membershipAllowsIn(membership, "events", "invoices.issue");
  const canViewSponsors = membershipAllowsIn(membership, "events", "sponsors.manage");
  const canViewTasks = membershipAllowsIn(membership, "events", "bookings.tasks.manage");
  // Stock is the client's own kit wherever it is going, so it is asked across both halves, which is
  // how the server judges the reservation routes too.
  const canViewInventory = membershipAllows(membership, "inventory.operate");
  const { selectedVenueId: venueId, loading: venueLoading } = useVenueScope();
  const today = startOfDay(new Date());
  const horizon = addDays(today, 365);
  const listQuery = queryString({ from: formatISO(today), to: formatISO(horizon), venueId });

  const events = useQuery({
    queryKey: ["events", "home-upcoming", listQuery],
    queryFn: () => api<ListResponse<EventRecord>>(`/api/v1/events${listQuery}`),
    enabled: Boolean(membership && venueId),
  });
  const upcoming = (events.data?.items ?? [])
    .filter((event) => !terminalStatuses.has(event.status))
    .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime())
    .map(toRecord);
  const next = upcoming[0];
  const nextID = next?.id ?? "";
  const invoices = useQuery({
    queryKey: ["invoices", "home-next-event", nextID],
    queryFn: () =>
      api<ListResponse<Invoice>>(
        `/api/v1/invoices?eventId=${encodeURIComponent(nextID)}&limit=100&offset=0`,
      ),
    enabled: Boolean(nextID && canViewFinance),
  });
  const sponsorships = useQuery({
    queryKey: ["sponsorships", "home-next-event", nextID],
    queryFn: () =>
      api<ListResponse<Sponsorship>>(`/api/v1/sponsorships?eventId=${encodeURIComponent(nextID)}`),
    enabled: Boolean(nextID && canViewSponsors),
  });
  const tasks = useQuery({
    queryKey: ["events", nextID, "tasks"],
    queryFn: () => api<ListResponse<BookingTask>>(`/api/v1/events/${nextID}/tasks`),
    enabled: Boolean(nextID && canViewTasks),
  });
  const reservations = useQuery({
    queryKey: ["inventory", "reservations", "event", nextID],
    queryFn: () =>
      api<ListResponse<InventoryReservation>>(`/api/v1/events/${nextID}/inventory-reservations`),
    enabled: Boolean(nextID && canViewInventory),
  });
  const assetReservations = useQuery({
    queryKey: ["inventory", "asset-reservations", "event", nextID],
    queryFn: () =>
      api<ListResponse<AssetReservation>>(`/api/v1/events/${nextID}/asset-reservations`),
    enabled: Boolean(nextID && canViewInventory),
  });

  if (venueLoading || (Boolean(venueId) && events.isPending)) {
    return <DashboardSkeleton title={timeGreeting()} />;
  }
  if (events.error) return <ErrorPanel error={events.error} />;

  const firstName = auth.session?.user.displayName.split(" ")[0] ?? "there";
  const eventInvoices = (invoices.data?.items ?? []).filter(
    (invoice) => invoice.eventId === nextID,
  );
  const outstandingMinor = eventInvoices.reduce(
    (total, invoice) => total + invoice.balanceMinor,
    0,
  );
  const eventSponsors = (sponsorships.data?.items ?? []).filter(
    (sponsorship) => sponsorship.eventId === nextID,
  ).length;
  const openTasks = (tasks.data?.items ?? []).filter(
    (task) => task.status !== "completed" && task.status !== "cancelled",
  ).length;
  const reservedResources =
    (reservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length +
    (assetReservations.data?.items ?? []).filter((item) => item.status !== "cancelled").length;

  const tiles: ReadinessTile[] = next
    ? [
        {
          key: "spaces",
          label: "Spaces",
          icon: <MapPin aria-hidden="true" />,
          value: next.locationNames.length || "Pending",
        },
        {
          key: "guests",
          label: "Expected guests",
          icon: <Users aria-hidden="true" />,
          value: next.attendeeCount ?? "Pending",
        },
        ...(canViewTasks
          ? [
              {
                key: "tasks",
                label: "Open tasks",
                icon: <ListChecks aria-hidden="true" />,
                value: queryCount(tasks.isLoading, tasks.error, openTasks),
                attention: true,
              },
            ]
          : []),
        ...(canViewFinance
          ? [
              {
                key: "balance",
                label: "Balance due",
                icon: <CircleDollarSign aria-hidden="true" />,
                value: invoices.isLoading
                  ? "Loading"
                  : invoices.error
                    ? "Unavailable"
                    : formatMoney(outstandingMinor, eventInvoices[0]?.currency ?? "USD"),
                attention: outstandingMinor > 0,
              },
            ]
          : []),
        ...(canViewSponsors
          ? [
              {
                key: "sponsors",
                label: "Sponsor agreements",
                icon: <Handshake aria-hidden="true" />,
                value: queryCount(sponsorships.isLoading, sponsorships.error, eventSponsors),
              },
            ]
          : []),
        ...(canViewInventory
          ? [
              {
                key: "resources",
                label: "Reserved resources",
                icon: <Boxes aria-hidden="true" />,
                value: queryCount(
                  reservations.isLoading || assetReservations.isLoading,
                  reservations.error ?? assetReservations.error,
                  reservedResources,
                ),
              },
            ]
          : []),
      ]
    : [];

  return (
    <>
      <PageHeader
        title={`${timeGreeting()}, ${firstName}`}
        description="Your next event comes first, followed by the work and commercial signals that need attention."
        actions={
          <Link className="button secondary" to="/calendar">
            Open calendar <ArrowRight size={17} />
          </Link>
        }
      />

      <NextUpSection
        record={next}
        labels={{
          eyebrow: "Event command view",
          heading: "Next event",
          openAria: "Open event overview for",
          emptyTitle: "No upcoming events",
          emptyDescription:
            "Create an event to start coordinating the space, the customer, finance, sponsors, and the vendors brought in for it.",
          emptyActionLabel: "Create event",
          emptyActionTo: "/events/new",
        }}
      />

      {next ? (
        <ReadinessSection
          eyebrow="At a glance"
          heading="Event readiness"
          briefTo={next.path}
          briefLabel="See the full event brief"
          tiles={tiles}
        />
      ) : null}

      <UpcomingSection
        records={upcoming.slice(1, 6)}
        title="Coming up after this"
        description="The next events at this venue."
        emptyTitle="Nothing else scheduled"
        emptyDescription="New events will appear here as they are scheduled."
      />
    </>
  );
}
